'use client';

import React, { useEffect, useState } from 'react';
import {
    FiEdit,
    FiTrash2,
    FiChevronLeft,
    FiChevronRight,
} from 'react-icons/fi';
import { DatePicker, ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import useColors from '../../hooks/useColors';
import { Images } from '../../config';
import moment from 'moment';
import { useSelector } from 'react-redux';

// const resources = [
//     { id: 1, name: 'Room A' },
//     { id: 2, name: 'Room B' },
//     { id: 3, name: 'Room C' },
//     { id: 3, name: 'Room C' },
//     { id: 3, name: 'Room C' },
//     { id: 3, name: 'Room C' },
//     { id: 3, name: 'Room C' },
//     { id: 3, name: 'Room C' },
//     { id: 3, name: 'Room C' },
//     { id: 3, name: 'Room C' },
//     { id: 3, name: 'Room C' },
//     { id: 3, name: 'Room C' },
// ];

// const sampleEvents = [
//     {
//         id: 1,
//         resourceId: 1,
//         title: 'Meeting',
//         start: new Date('2026-03-31T15:00:00'),
//         end: new Date('2026-03-31T19:00:00'),
//     },
//     {
//         id: 2,
//         resourceId: 1,
//         title: 'Workshop',
//         start: new Date('2026-03-31T16:30:00'),
//         end: new Date('2026-03-31T18:00:00'),
//     },
//     {
//         id: 3,
//         resourceId: 1,
//         title: 'Multi Day Event',
//         start: new Date('2026-03-31T10:00:00'),
//         end: new Date('2026-04-05T18:00:00'),
//     },
//     {
//         id: 4,
//         resourceId: 1,
//         title: 'Multi Day Event',
//         start: new Date('2026-03-31T10:00:00'),
//         end: new Date('2026-04-02T18:00:00'),
//     },
// ];

const hours = Array.from({ length: 24 }, (_, i) => i);
const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function CustomCalendar({
    schedulerData,
    paginationMetadata,
    onLoadMore,
    isLoadingMore = false,
    onResourceClick,
    itsAllowDrag = false,
    shiftTypeHandler,
    onViewChange,
    onDateChange,
}) {
    const [draggingEvent, setDraggingEvent] = useState(null);
    const [ghostPosition, setGhostPosition] = useState(null);
    let resources = schedulerData?.resources || [];
    let sampleEvents = schedulerData?.events || [];
    console.log(schedulerData, sampleEvents);
    if (sampleEvents && Array.isArray(sampleEvents)) {
        sampleEvents = sampleEvents
            .map((item) => {
                let st = item.start;
                let en = item.end;

                if (!st || !en) {
                    if (item.duration && typeof item.duration === 'string') {
                        const parts = item.duration.split(' - ');
                        if (parts.length === 2) {
                            if (!st) st = parts[0];
                            if (!en) en = parts[1];
                        }
                    }
                }

                if (!st && item.shift_start) st = item.shift_start;
                if (!en && item.shift_end) en = item.shift_end;

                return {
                    ...item,
                    start: st,
                    end: en,
                };
            })
            .filter((item) => item.start && item.end);
    }
    const { colorPrimary, colorSecondary } = useColors();
    const dateFormat = useSelector((state) => state.AppReducer?.dateFormat);
    const timeFormat = useSelector((state) => state.AppReducer?.timeFormat);
    const primaryStr = colorPrimary;
    const secondaryStr = colorSecondary;
    const startOfDay = (date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    };
    const [view, setView] = useState('week');
    const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));
    const [hoveredEvent, setHoveredEvent] = useState(null);
    const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
    const [activeEvent, setActiveEvent] = useState(null);

    // Keep parent in sync with the selected date whenever it changes
    useEffect(() => {
        onDateChange?.(selectedDate);
    }, [selectedDate]);

    // Compute events visible in current day view
    const getEventsForDay = (date) => {
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(date);
        dayEnd.setHours(23, 59, 59, 999);
        return sampleEvents.filter(
            (e) => new Date(e.end) > dayStart && new Date(e.start) < dayEnd,
        );
    };

    // Compute events visible in current week view
    const getEventsForWeek = (date) => {
        const weekStart = startOfDay(new Date(date));
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 7);
        return sampleEvents.filter(
            (e) => new Date(e.end) > weekStart && new Date(e.start) < weekEnd,
        );
    };

    const visibleEventCount =
        view === 'day'
            ? getEventsForDay(selectedDate).length
            : getEventsForWeek(selectedDate).length;

    const isOpenShiftEvent = (event) =>
        event?.openShift === true || event?.completeRecord?.openShift === true;

    const getOpenShiftEvents = () => sampleEvents.filter(isOpenShiftEvent);

    const getAssignedResourceEvents = (resourceId) =>
        sampleEvents.filter(
            (event) =>
                !isOpenShiftEvent(event) &&
                (event.resourceId === resourceId ||
                    event.userId === resourceId),
        );

    const getDayBoundedEvent = (event, date) => {
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);

        const dayEnd = new Date(date);
        dayEnd.setHours(23, 59, 59, 999);

        // Convert event times to Date objects (handles UTC strings, moment objects, timestamps)
        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end);

        // If event doesn't touch this day at all → ignore
        if (eventEnd <= dayStart || eventStart >= dayEnd) {
            return null;
        }

        return {
            ...event,
            start: new Date(Math.max(eventStart.getTime(), dayStart.getTime())),
            end: new Date(Math.min(eventEnd.getTime(), dayEnd.getTime())),
        };
    };

    const arrangeEvents = (events) => {
        // Step 1: sort by start time
        const sorted = [...events].sort((a, b) => a.startVal - b.startVal);

        const layers = [];

        sorted.forEach((event) => {
            let placed = false;

            // Try to fit into existing layers
            for (let i = 0; i < layers.length; i++) {
                const lastEventInLayer = layers[i][layers[i].length - 1];

                // ✅ Only check LAST event in that layer (important optimization)
                if (event.startVal >= lastEventInLayer.endVal) {
                    layers[i].push(event);
                    placed = true;
                    break;
                }
            }

            // If no layer fits → create new one
            if (!placed) {
                layers.push([event]);
            }
        });

        return layers;
    };

    const getEventColorForId = (eventId) => {
        const colors = ['#6366f1', '#ec4899', '#06b6d4', '#8b5cf6', '#f59e0b'];
        return colors[eventId % colors.length];
    };

    const handleEditEvent = (event) => {
        if (event.shiftDetailHandler) {
            event.shiftDetailHandler(event.completeRecord);
            return;
        }

        // if (!onOpenCreateShiftDrawer) {
        //     // alert(`Edit: ${event.title}`);
        //     return;
        // } else {
        //     onOpenCreateShiftDrawer(event);
        // }
    };

    const handleDeleteEvent = (event) => {
        alert(`Delete: ${event.title}`);
    };

    const arrangeWeekEvents = (events, weekStart) => {
        const layers = [];

        const getDayIndex = (date) => {
            const d = startOfDay(date);
            return Math.floor((d - weekStart) / (1000 * 60 * 60 * 24));
        };

        const getEventSpan = (event) => {
            let startDay = getDayIndex(event.start);
            let endDay =
                Math.ceil((event.end - weekStart) / (1000 * 60 * 60 * 24)) - 1;

            startDay = Math.max(0, Math.min(6, startDay));
            endDay = Math.max(0, Math.min(6, endDay));

            return { startDay, endDay };
        };

        const splitEvents = [];
        events.forEach((event) => {
            const { startDay, endDay } = getEventSpan(event);
            for (let d = startDay; d <= endDay; d++) {
                splitEvents.push({
                    ...event,
                    startDay: d,
                    endDay: d,
                });
            }
        });

        splitEvents.sort((a, b) => a.startDay - b.startDay);

        splitEvents.forEach((event) => {
            let placed = false;

            for (let i = 0; i < layers.length; i++) {
                if (
                    !layers[i].some(
                        (e) =>
                            !(
                                event.endDay < e.startDay ||
                                event.startDay > e.endDay
                            ),
                    )
                ) {
                    layers[i].push(event);
                    placed = true;
                    break;
                }
            }

            if (!placed) layers.push([event]);
        });

        return layers;
    };
    const normalizeEventForDay = (event, date) => {
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);

        const dayEnd = new Date(date);
        dayEnd.setHours(23, 59, 59, 999);

        // Convert event times to Date objects (handles UTC strings, moment objects, timestamps)
        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end);

        // ignore if not touching this day
        if (eventEnd <= dayStart || eventStart >= dayEnd) {
            return [];
        }

        const start = new Date(
            Math.max(eventStart.getTime(), dayStart.getTime()),
        );
        const end = new Date(Math.min(eventEnd.getTime(), dayEnd.getTime()));

        const startVal = start.getHours() + start.getMinutes() / 60;
        const endVal = end.getHours() + end.getMinutes() / 60;

        // 👇 if crosses midnight INSIDE SAME DAY VIEW
        if (endVal <= startVal) {
            return [
                {
                    ...event,
                    start,
                    _splitPart: 1,
                    end: new Date(new Date(date).setHours(23, 59, 59, 999)),
                    startVal,
                    endVal: 24,
                },
                {
                    ...event,
                    _splitPart: 2,
                    start: new Date(new Date(date).setHours(0, 0, 0, 0)),
                    end,
                    startVal: 0,
                    endVal,
                },
            ];
        }

        return [{ ...event, start, end, startVal, endVal }];
    };
    const handleDrop = (
        e,
        baseDate,
        isDayView = false,
        isOpenShift = undefined,
    ) => {
        e.preventDefault();

        const eventData = JSON.parse(e.dataTransfer.getData('event'));
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const height = rect.height;

        // 🧮 Convert vertical position → time
        const totalMinutes = (y / height) * 24 * 60;

        const hour = Math.floor(totalMinutes / 60);
        const remainderMinutes = totalMinutes % 60;

        // snap to 15-min grid INSIDE the hour
        const minutes = Math.round(remainderMinutes / 15) * 15;

        // handle edge case (e.g. 10:60 → 11:00)
        const finalHour = minutes === 60 ? hour + 1 : hour;
        const finalMinutes = minutes === 60 ? 0 : minutes;

        const dropDate = new Date(baseDate);
        dropDate.setHours(finalHour, finalMinutes, 0, 0);
        console.log(
            ghostPosition?.ResourceData,
            '*********',
            hoveredEvent.completeRecord.openShift,
            'npx react-native-asset',
            isOpenShift == undefined || isOpenShift == null
                ? hoveredEvent?.completeRecord?.openShift
                : isOpenShift,
        );
        hoveredEvent?.shiftDetailHandler(
            {
                ...hoveredEvent?.completeRecord,
                ResourceAssignTo: ghostPosition?.ResourceData?.id, //ghostPosition?.ResourceData?.email,
                ResetTime: ghostPosition?.SlotTimeDate || baseDate,
                isDayView,
                isNormalToOpen: ghostPosition?.ResourceData?.id ? true : false,
            },
            true,
            ghostPosition?.ResourceData?.id
                ? isOpenShift
                : isOpenShift == undefined || isOpenShift == null
                    ? hoveredEvent?.completeRecord?.openShift
                    : isOpenShift,
        );

        // console.log(dropDate.toDateString(),
        //     hour,
        //     minutes
        //         .toString()
        //         .padStart(2, '0'), isDayView, isOpenShift)
        // console.log(eventData)

        // alert(
        //     `Dropped on:\n${dropDate.toDateString()} at ${hour}:${minutes
        //         .toString()
        //         .padStart(2, '0')}`
        // );
    };

    const updateGhost = (e, baseDate, SlotTimeDate, ResourceData = {}) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const height = rect.height;

        const totalMinutes = (y / height) * 24 * 60;
        const hour = Math.floor(totalMinutes / 60);
        const minutes = Math.floor(totalMinutes % 60);

        const dropDate = new Date(baseDate);
        dropDate.setHours(hour, minutes, 0, 0);

        setGhostPosition({
            x: e.clientX,
            y: e.clientY,
            date: dropDate,
            SlotTimeDate: SlotTimeDate || moment(dropDate).format('YYYY-MM-DD'),
            ResourceData: ResourceData ? JSON.parse(ResourceData) : {},
        });
    };

    const renderEvent = (event, styleProps) => {
        const BG = primaryStr;
        const isDraft =
            event.completeRecord?.isPublished === false ||
            event.completeRecord?.published === false;
        const background = isDraft
            ? `repeating-linear-gradient(-45deg, ${BG}, ${BG} 6px, rgba(0,0,0,0.45) 6px, rgba(0,0,0,0.45) 12px)`
            : BG;
        return (
            <div
                key={`${event.id}-${event.startDay ?? ''}-${event._splitPart ?? ''}`}
                // onMouseEnter={(e) => {
                //     const rect = e.currentTarget.getBoundingClientRect();
                //     setHoverPosition({
                //         x: rect.left + rect.width / 2,
                //         y: rect.top,
                //     });
                //     setHoveredEvent(event);
                // }}
                onMouseMove={(e) => {
                    setHoverPosition({
                        x: e.clientX,
                        y: e.clientY,
                    });
                    setHoveredEvent(event);
                }}
                onMouseLeave={() => setHoveredEvent(null)}
                // onClick={() => setActiveEvent(event)}
                onClick={(e) => {
                    e.stopPropagation();
                    setActiveEvent(event);
                    handleEditEvent(event);
                }}
                // draggable
                // onDragStart={(e) => {
                //     e.dataTransfer.setData('event', JSON.stringify(event));
                // }}
                // draggable={!isOpenShiftEvent(event)}
                draggable={itsAllowDrag}
                onDragStart={(e) => {
                    const data = JSON.stringify(event);
                    e.dataTransfer.setData('event', data);

                    // const start = moment(event?.duration.split(' ')[1], 'HH:mm:ss');
                    // const end = moment(event?.duration.split(' ')[4], 'HH:mm:ss');

                    // const diffMinutes = end.diff(start, 'minutes');

                    new Date(event.end) - new Date(event.start);

                    setDraggingEvent({
                        ...event,
                        // durationMs,
                    });
                }}
                onDragEnd={() => {
                    setDraggingEvent(null);
                    setGhostPosition(null);
                }}
                className="text-white text-xs px-2.5 py-1.5 rounded-md cursor-pointer absolute font-medium overflow-hidden whitespace-nowrap flex items-center border border-opacity-20 border-white"
                style={{
                    ...styleProps,
                    background: background, //getEventColorForId(event.id),
                    boxShadow:
                        hoveredEvent?.id === event.id &&
                            hoveredEvent?.startDay === event.startDay &&
                            hoveredEvent?._splitPart === event._splitPart
                            ? 'inset rgba(0, 0, 0, 0.15) 0px 0px 20px 17px'
                            : '0 2px 6px rgba(0,0,0,0.15)',
                    borderLeft:
                        event._splitPart === 2 ? 'none' : '4px solid #fff',
                    display: 'flex',
                    alignItems: 'center',
                    zIndex: 5,
                    borderRadius: event._splitPart
                        ? event._splitPart === 2
                            ? '0px 6px 6px 0px'
                            : '6px 0px 0px 6px'
                        : '6px',
                    borderBottom: event._splitPart ? '3px solid #fff' : 'none',
                }}
            >
                <span
                    className="truncate"
                    style={{ color: BG.includes('#ff') ? 'black' : 'white' }}
                >
                    {/* 2026-03-31 00:00:00 */}
                    {moment(event?.duration.split(' ')[1], 'hh:mm:ss').format(
                        timeFormat,
                    )}
                    {' - '}
                    {moment(event?.duration.split(' ')[4], 'hh:mm:ss').format(
                        timeFormat,
                    )}{' '}
                    {/* {moment(event?.start, 'yyyy-mm-dd hh:mm:ss ').format(
                        'hh:mm a',
                    ) +
                        ' - ' +
                        moment(event?.end, 'yyyy-mm-dd hh:mm:ss ').format(
                            'hh:mm a',
                        )}{' '} */}
                    at {event.property}
                </span>

                {/* Edit & Delete icons on hover */}
                {hoveredEvent?.id === event.id && (
                    <div className="ml-auto flex gap-1 items-center text-white">
                        {event.shiftDetailHandler && (
                            <FiEdit
                                className="w-3 h-3 cursor-pointer"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditEvent(event);
                                }}
                            />
                        )}
                        {/* <FiTrash2
                            className="w-3 h-3 cursor-pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteEvent(event);
                            }}
                        /> */}
                    </div>
                )}
            </div>
        );
    };
    const renderDayView = () => {
        const expandEventsForDay = (events, date) => {
            return events.flatMap((e) => normalizeEventForDay(e, date));
        };
        const openShiftEventsDay = expandEventsForDay(
            getOpenShiftEvents(),
            selectedDate,
        );
        const openShiftLayersDay = arrangeEvents(openShiftEventsDay);
        const openShiftRowHeightDay = Math.max(
            40,
            openShiftLayersDay.length * 50,
        );

        // Show all resources, even when they have no events for the selected day.
        const activeResourcesDay = resources;

        return (
            <div className="overflow-auto border">
                <div className="flex">
                    {/* Resource column */}
                    <div>
                        <div className="h-12 w-[150px] flex items-center justify-center font-semibold border-r border-b bg-gray-100">
                            Resource
                        </div>
                        {itsAllowDrag ? (
                            <div
                                className="w-[150px] flex items-center justify-center font-semibold border-r border-b bg-green-100"
                                style={{
                                    minHeight: `${openShiftRowHeightDay}px`,
                                }}
                            >
                                OpenShift
                            </div>
                        ) : null}
                        {activeResourcesDay.map((r) => {
                            const toTimeValue = (date) => {
                                return date.getHours() + date.getMinutes() / 60;
                            };
                            const expandEventsForDay = (events, date) => {
                                return events.flatMap((e) =>
                                    normalizeEventForDay(e, date),
                                );
                            };
                            const resourceEvents = expandEventsForDay(
                                getAssignedResourceEvents(r.id),
                                selectedDate,
                            );

                            const layers = arrangeEvents(resourceEvents);
                            const rowHeight = Math.max(40, layers.length * 50);

                            return (
                                <div
                                    key={r.id}
                                    className="w-[150px] flex items-center justify-start px-3 border-r border-b bg-gray-50 text-sm font-medium"
                                    style={{
                                        minHeight: `${rowHeight}px`,
                                        marginTop: 1,
                                        whiteSpace: 'break-spaces',
                                        textOverflow: 'ellipsis',
                                        cursor: onResourceClick
                                            ? 'pointer'
                                            : 'default',
                                    }}
                                    onClick={() =>
                                        onResourceClick && onResourceClick(r)
                                    }
                                >
                                    <div
                                        style={{
                                            // height: '40px',
                                            display: 'flex',
                                            position: 'relative',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 30,
                                                height: 30,
                                                marginRight: 4,
                                            }}
                                        >
                                            <img
                                                src={
                                                    r?.image
                                                        ? r?.image
                                                        : Images.Avatar
                                                }
                                                style={{
                                                    width: 30,
                                                    height: 30,
                                                    // borderRadius: '50%',
                                                    objectFit: 'cover',
                                                    marginRight: '4px',
                                                    boxShadow:
                                                        'rgba(0, 0, 0, 0.16) 0px 3px 6px, rgba(0, 0, 0, 0.23) 0px 3px 6px',
                                                }}
                                            />
                                        </div>
                                        {/* {r.count ? (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    bottom: '2px',
                                                    right: '2px',
                                                    backgroundColor: 'red', // Badge background
                                                    color: '#fff',
                                                    borderRadius: '50%',
                                                    width: '12px',
                                                    height: '12px',
                                                    fontSize: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    zIndex: 1,
                                                }}
                                            >
                                                {r.count}
                                            </div>
                                        ) : null} */}
                                    </div>
                                    <span
                                        className="truncate w-[146px]"
                                        style={{
                                            paddingLeft: '3px',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {r.name}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {/* Time grid container */}
                    <div className="flex-1 overflow-x-auto">
                        <div className="flex bg-gray-50 border-b">
                            {hours.map((h) => (
                                <div
                                    key={h}
                                    className="h-12 text-xs flex items-center justify-center border-r font-semibold"
                                    style={{ flex: 1 }}
                                >
                                    {moment()
                                        .hour(h)
                                        .minute(0)
                                        .format(timeFormat)}
                                </div>
                            ))}
                        </div>

                        {itsAllowDrag ? (
                            <div
                                className="relative flex flex-1 bg-green-100 border-b"
                                style={{
                                    minHeight: `${openShiftRowHeightDay}px`,
                                }}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    updateGhost(
                                        e,
                                        selectedDate,
                                        e.target.dataset.time,
                                        null,
                                    );
                                    e.target.style.boxShadow =
                                        'inset #03030354 0px 1px 18px 2px';
                                }}
                                onDragLeaveCapture={(e) => {
                                    e.target.style.boxShadow = '';
                                }}
                                onDrop={(e) => {
                                    e.target.style.boxShadow = '';
                                    handleDrop(e, selectedDate, true, true);
                                }}
                            >
                                {hours.map((h) => (
                                    <div
                                        key={h}
                                        data-time={h}
                                        onClick={(e) => {
                                            shiftTypeHandler?.('open', {
                                                date: selectedDate,
                                                time: h,
                                                // resourceId: r?.id
                                            })
                                        }}
                                        onMouseOver={(e) => {
                                            e.target.style.cursor = 'pointer';
                                            e.target.style.boxShadow =
                                                'inset #03030354 0px 1px 18px 2px';
                                            e.target.style.border =
                                                'dashed 1px';
                                            e.target.innerText = '+';
                                        }}
                                        onMouseOut={(e) => {
                                            e.target.style.cursor = null;
                                            e.target.innerText = '';
                                            e.target.style.boxShadow = null;
                                            e.target.style.border = null;
                                        }}
                                        className="border-r border-gray-200 flex  items-center justify-center"
                                        style={{
                                            flex: 1,
                                            minHeight: `${openShiftRowHeightDay}px`,
                                        }}
                                    />
                                ))}

                                {openShiftLayersDay.map((layer, layerIdx) =>
                                    layer.map((event) => {
                                        const startValue =
                                            event.start.getHours() +
                                            event.start.getMinutes() / 60;
                                        let endValue =
                                            event.end.getHours() +
                                            event.end.getMinutes() / 60;

                                        if (endValue <= startValue) {
                                            endValue += 24;
                                        }

                                        return renderEvent(event, {
                                            left: `${(startValue * 100) / 24}%`,
                                            width: `${((endValue - startValue) *
                                                100) /
                                                24
                                                }%`,
                                            top: `${layerIdx * 50}px`,
                                            // height: '40px',
                                        });
                                    }),
                                )}
                            </div>
                        ) : null}

                        {activeResourcesDay.map((r) => {
                            const toTimeValue = (date) => {
                                return date.getHours() + date.getMinutes() / 60;
                            };
                            const expandEventsForDay = (events, date) => {
                                return events.flatMap((e) =>
                                    normalizeEventForDay(e, date),
                                );
                            };
                            const resourceEvents = expandEventsForDay(
                                getAssignedResourceEvents(r.id),
                                selectedDate,
                            );

                            const layers = arrangeEvents(resourceEvents);
                            const rowHeight = Math.max(40, layers.length * 50);

                            return (
                                <div
                                    key={r.id}
                                    className="relative flex border-b"
                                    style={{ minHeight: `${rowHeight}px` }}
                                >
                                    <div
                                        className="flex flex-1"
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            updateGhost(
                                                e,
                                                selectedDate,
                                                e.target.dataset.time,
                                                e.target.dataset.resource,
                                            );
                                            // console.log(
                                            //     e.target.dataset.resource,
                                            //     'Day View',
                                            // );

                                            e.target.style.boxShadow =
                                                'inset #03030354 0px 1px 18px 2px';
                                        }}
                                        onDragLeaveCapture={(e) => {
                                            e.target.style.boxShadow = '';
                                        }}
                                        onDrop={(e) => {
                                            e.target.style.boxShadow = '';
                                            handleDrop(
                                                e,
                                                selectedDate,
                                                true,
                                                false,
                                            );
                                        }}
                                    >
                                        {hours.map((h) => (
                                            <div
                                                key={h}
                                                data-resource={JSON.stringify(
                                                    r,
                                                )}
                                                data-time={h}
                                                onClick={(e) => {
                                                    shiftTypeHandler?.('normal', {
                                                        date: selectedDate,
                                                        time: h,
                                                        resourceId: r?.id
                                                    })
                                                }}
                                                onMouseOver={shiftTypeHandler ? (e) => {
                                                    e.target.style.cursor =
                                                        'pointer';
                                                    e.target.style.boxShadow =
                                                        'inset #03030354 0px 1px 18px 2px';
                                                    e.target.style.border =
                                                        'dashed 1px';
                                                    e.target.innerText = '+';
                                                } : undefined}
                                                onMouseOut={shiftTypeHandler ? (e) => {
                                                    e.target.style.cursor =
                                                        null;
                                                    e.target.innerText = '';
                                                    e.target.style.boxShadow =
                                                        null;
                                                    e.target.style.border =
                                                        null;
                                                } : undefined}
                                                className="border-r border-gray-200 flex  items-center justify-center"
                                                style={{
                                                    minHeight: `${rowHeight}px`,
                                                    flex: 1,
                                                }}
                                            />
                                        ))}
                                    </div>

                                    {layers.map((layer, layerIdx) =>
                                        layer.map((event) => {
                                            const startHour =
                                                event.start.getHours();
                                            const startMin =
                                                event.start.getMinutes();
                                            const endHour =
                                                event.end.getHours();
                                            const endMin =
                                                event.end.getMinutes();

                                            const startValue =
                                                startHour + startMin / 60;
                                            let endValue =
                                                endHour + endMin / 60;

                                            // 👇 KEY: if event crosses midnight
                                            if (endValue <= startValue) {
                                                endValue += 24;
                                            }

                                            const startPercent =
                                                (startValue * 100) / 24;
                                            const widthPercent =
                                                ((endValue - startValue) *
                                                    100) /
                                                24;
                                            const top = layerIdx * 50;

                                            return renderEvent(event, {
                                                left: `${startPercent}%`,
                                                width: `${widthPercent}%`,
                                                top: `${top}px`,
                                                // height: '40px',
                                            });
                                            // }
                                        }),
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    const renderWeekView = () => {
        const weekStart = startOfDay(new Date(selectedDate));
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 7);

        const openShiftEventsWeek = getOpenShiftEvents().filter(
            (e) => new Date(e.start) < weekEnd && new Date(e.end) > weekStart,
        );
        const openShiftLayersWeek = arrangeWeekEvents(
            openShiftEventsWeek,
            weekStart,
        );
        const openShiftRowHeightWeek = Math.max(
            40,
            openShiftLayersWeek.length * 32 + 10,
        );

        // Show all resources, even when they have no events for the selected week.
        const activeResourcesWeek = resources;

        return (
            <div className="overflow-auto border w-full">
                <div className="flex">
                    {/* Resource column */}
                    <div>
                        <div className="h-12 w-[150px] flex items-center justify-center font-semibold border-r border-b bg-gray-100">
                            Resource
                        </div>
                        {itsAllowDrag ? (
                            <div
                                className="w-[150px] flex items-center justify-center font-semibold border-r border-b bg-green-100"
                                style={{
                                    minHeight: `${openShiftRowHeightWeek}px`,
                                }}
                            >
                                Open Shifts
                            </div>
                        ) : null}
                        {activeResourcesWeek.map((r) => {
                            const resourceEvents = getAssignedResourceEvents(
                                r.id,
                            ).filter(
                                (e) =>
                                    new Date(e.start) < weekEnd &&
                                    new Date(e.end) > weekStart,
                            );
                            const layers = arrangeWeekEvents(
                                resourceEvents,
                                weekStart,
                            );
                            const rowHeight = Math.max(
                                40,
                                layers.length * 32 + 10,
                            );

                            return (
                                <div
                                    key={r.id}
                                    className="w-[150px] flex items-center justify-start px-3 border-r border-b bg-gray-50 text-sm font-medium"
                                    style={{
                                        minHeight: `${rowHeight - 1}px`,
                                        marginTop: 1,
                                        overflow: 'hidden',
                                        whiteSpace: 'break-spaces',
                                        cursor: onResourceClick
                                            ? 'pointer'
                                            : 'default',
                                    }}
                                    onClick={() =>
                                        onResourceClick && onResourceClick(r)
                                    }
                                >
                                    <div
                                        style={{
                                            // height: '40px',
                                            display: 'flex',
                                            position: 'relative',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 30,
                                                height: 30,
                                                marginRight: 4,
                                            }}
                                        >
                                            <img
                                                src={
                                                    r?.image
                                                        ? r?.image
                                                        : Images.Avatar
                                                }
                                                style={{
                                                    width: 30,
                                                    height: 30,
                                                    // borderRadius: '100%',
                                                    objectFit: 'cover',
                                                    marginRight: '4px',
                                                    boxShadow:
                                                        'rgba(0, 0, 0, 0.16) 0px 3px 6px, rgba(0, 0, 0, 0.23) 0px 3px 6px',
                                                }}
                                            />
                                        </div>
                                        {/* {r.count ? (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    bottom: '2px',
                                                    right: '2px',
                                                    backgroundColor: 'red', // Badge background
                                                    color: '#fff',
                                                    borderRadius: '50%',
                                                    width: '12px',
                                                    height: '12px',
                                                    fontSize: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    zIndex: 1,
                                                }}
                                            >
                                                {r.count}
                                            </div>
                                        ) : null} */}
                                    </div>
                                    <span
                                        className="truncate w-[146px]"
                                        style={{
                                            paddingLeft: '3px',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {r.name}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    <div className="flex-1 w-full">
                        <div className="grid grid-cols-7 w-full">
                            {Array.from({ length: 7 }).map((_, i) => {
                                const day = new Date(weekStart);
                                day.setDate(weekStart.getDate() + i);
                                return (
                                    <div
                                        key={i}
                                        className="border-b border-r text-center font-semibold bg-gray-50 h-12 flex items-center justify-center"
                                    >
                                        <div className="flex flex-col">
                                            <span>{days[day.getDay()]}</span>
                                            <span className="font-normal text-[11px]">
                                                {moment(day).format(dateFormat)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {itsAllowDrag ? (
                            <div
                                className="relative grid grid-cols-7 w-full bg-green-100 border-b"
                                style={{
                                    minHeight: `${openShiftRowHeightWeek}px`,
                                }}
                            >
                                {Array.from({ length: 7 }).map((_, i) => {
                                    const day = new Date(weekStart);
                                    day.setDate(weekStart.getDate() + i);
                                    return (
                                        <div
                                            key={i}
                                            className="border-r text-center font-semibold relative flex  items-center justify-center"
                                            onClick={(e) => {
                                                const d = new Date(weekStart);
                                                d.setDate(d.getDate() + i);
                                                shiftTypeHandler?.('open', {
                                                    date: d,
                                                    // resourceId: r?.id
                                                })
                                            }}
                                            onMouseOver={(e) => {
                                                e.target.style.cursor =
                                                    'pointer';
                                                e.target.style.boxShadow =
                                                    'inset #03030354 0px 1px 18px 2px';
                                                e.target.style.border =
                                                    'dashed 1px';
                                                e.target.innerText = '+';
                                            }}
                                            onMouseOut={(e) => {
                                                e.target.style.cursor = null;
                                                e.target.innerText = '';
                                                e.target.style.boxShadow = null;
                                                e.target.style.border = null;
                                            }}
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                const day = new Date(weekStart);
                                                day.setDate(
                                                    weekStart.getDate() + i,
                                                );
                                                updateGhost(e, day, null, null);
                                                e.target.style.boxShadow =
                                                    'inset #03030354 0px 1px 18px 2px';
                                            }}
                                            onDragLeaveCapture={(e) => {
                                                e.target.style.boxShadow = '';
                                            }}
                                            onDrop={(e) => {
                                                const day = new Date(weekStart);
                                                day.setDate(
                                                    weekStart.getDate() + i,
                                                );
                                                e.target.style.boxShadow = '';
                                                handleDrop(e, day, false, true);
                                            }}
                                        />
                                    );
                                })}

                                {openShiftLayersWeek.map((layer, layerIdx) =>
                                    layer.map((event) => {
                                        const { startDay, endDay } = event;
                                        const colWidth = 100 / 7;
                                        const leftPercent = startDay * colWidth;
                                        const widthPercent =
                                            (endDay - startDay + 1) * colWidth;

                                        return renderEvent(event, {
                                            left: `calc(${leftPercent}% + 2px)`,
                                            width: `calc(${widthPercent}% - 4px)`,
                                            top: `${layerIdx * 32}px`,
                                            height: '26px',
                                        });
                                    }),
                                )}
                            </div>
                        ) : null}

                        {activeResourcesWeek.map((r) => {
                            const resourceEvents = getAssignedResourceEvents(
                                r.id,
                            ).filter(
                                (e) =>
                                    new Date(e.start) < weekEnd &&
                                    new Date(e.end) > weekStart,
                            );
                            const layers = arrangeWeekEvents(
                                resourceEvents,
                                weekStart,
                            );
                            const rowHeight = Math.max(
                                40,
                                layers.length * 32 + 10,
                            );
                            return (
                                <div
                                    key={r.id}
                                    className="relative grid grid-cols-7 w-full border-b"
                                    style={{ minHeight: `${rowHeight}px` }}
                                >
                                    {Array.from({ length: 7 }).map((_, i) => (
                                        <div
                                            key={i}
                                            data-resource={JSON.stringify(r)}
                                            className="border-r border-b border-gray-200 relative flex  items-center justify-center"
                                            onClick={(e) => {
                                                const d = new Date(weekStart);
                                                d.setDate(d.getDate() + i);
                                                shiftTypeHandler?.('normal', {
                                                    date: d,
                                                    resourceId: r?.id
                                                })
                                            }}
                                            onMouseOver={shiftTypeHandler ? (e) => {
                                                e.target.style.cursor =
                                                    'pointer';
                                                e.target.style.boxShadow =
                                                    'inset #03030354 0px 1px 18px 2px';
                                                e.target.style.border =
                                                    'dashed 1px';
                                                e.target.innerText = '+';
                                            } : undefined}
                                            onMouseOut={shiftTypeHandler ? (e) => {
                                                e.target.style.cursor = null;
                                                e.target.innerText = '';
                                                e.target.style.boxShadow = null;
                                                e.target.style.border = null;
                                            } : undefined}
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                const day = new Date(weekStart);
                                                day.setDate(
                                                    weekStart.getDate() + i,
                                                );
                                                updateGhost(
                                                    e,
                                                    day,
                                                    null,
                                                    e.target.dataset.resource,
                                                );
                                                e.target.style.boxShadow =
                                                    'inset #03030354 0px 1px 18px 2px';
                                            }}
                                            onDragLeaveCapture={(e) => {
                                                e.target.style.boxShadow = '';
                                            }}
                                            onDrop={(e) => {
                                                const day = new Date(weekStart);
                                                day.setDate(
                                                    weekStart.getDate() + i,
                                                );
                                                e.target.style.boxShadow = '';
                                                handleDrop(e, day, false);
                                            }}
                                        ></div>
                                    ))}

                                    {layers.map((layer, layerIdx) =>
                                        layer.map((event) => {
                                            const { startDay, endDay } = event;
                                            const colWidth = 100 / 7;
                                            const leftPercent =
                                                startDay * colWidth;
                                            const widthPercent =
                                                (endDay - startDay + 1) *
                                                colWidth;
                                            const top = layerIdx * 32;

                                            return renderEvent(event, {
                                                left: `calc(${leftPercent}% + 2px)`,
                                                width: `calc(${widthPercent}% - 4px)`,
                                                top: `${top}px`,
                                                height: '26px',
                                            });
                                        }),
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    const handlePrev = () => {
        const newDate = new Date(selectedDate);
        if (view === 'week') {
            newDate.setDate(newDate.getDate() - 7);
        } else {
            newDate.setDate(newDate.getDate() - 1);
        }
        setSelectedDate(newDate);
    };

    const handleNext = () => {
        const newDate = new Date(selectedDate);
        if (view === 'week') {
            newDate.setDate(newDate.getDate() + 7);
        } else {
            newDate.setDate(newDate.getDate() + 1);
        }
        setSelectedDate(newDate);
    };

    const formattedDateRange = () => {
        if (view === 'day') {
            return moment(selectedDate).format(dateFormat);
        } else {
            const weekStart = new Date(selectedDate);
            weekStart.setDate(weekStart.getDate() - weekStart.getDay());
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekEnd.getDate() + 6);

            return `${moment(weekStart).format(dateFormat)} - ${moment(weekEnd).format(dateFormat)}`;
        }
    };

    const Check = (value, FOR) => {
        return 'No schedules assigned!';
    };

    // ── Event Breakdown Strip (chips only) ──────────────────────────────────
    const renderEventBreakdown = () => {
        if (view === 'day') {
            const year = selectedDate.getFullYear();
            const month = selectedDate.getMonth();
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            const monthLabel = moment(selectedDate).format('MMM YYYY');

            const chips = [];
            for (let i = 1; i <= daysInMonth; i++) {
                const d = new Date(year, month, i);
                const count = getEventsForDay(d).length;
                if (count === 0) continue;
                const isSelected =
                    d.toDateString() === selectedDate.toDateString();
                chips.push({ day: d, count, isSelected, num: i });
            }

            if (chips.length === 0) return null;
            const totalMonth = chips.reduce((s, c) => s + c.count, 0);

            return (
                <div
                    style={{
                        borderTop: `1px solid ${primaryStr}22`,
                        borderBottom: `1px solid ${primaryStr}22`,
                        backgroundColor: `${primaryStr}07`,
                        padding: '7px 12px',
                        marginBottom: 8,
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 6,
                    }}
                >
                    <span
                        style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: primaryStr,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            marginRight: 4,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {monthLabel}
                    </span>

                    {chips.map(({ day, count, isSelected, num }) => (
                        <button
                            key={num}
                            onClick={() => setSelectedDate(startOfDay(day))}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                                padding: '3px 10px',
                                borderRadius: 20,
                                border: `1.5px solid ${isSelected ? primaryStr : primaryStr + '55'
                                    }`,
                                backgroundColor: isSelected
                                    ? primaryStr
                                    : `${primaryStr}12`,
                                color: isSelected ? '#fff' : primaryStr,
                                cursor: 'pointer',
                                fontSize: 11,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                transition: 'all 0.15s',
                            }}
                        >
                            <span>
                                {num} {moment(day).format('MMM')}
                            </span>
                            <span
                                style={{
                                    backgroundColor: isSelected
                                        ? 'rgba(255,255,255,0.3)'
                                        : primaryStr,
                                    color: '#fff',
                                    borderRadius: 10,
                                    padding: '0 5px',
                                    fontSize: 10,
                                    fontWeight: 700,
                                    lineHeight: '16px',
                                }}
                            >
                                {count}
                            </span>
                        </button>
                    ))}

                    <span
                        style={{
                            marginLeft: 'auto',
                            fontSize: 10,
                            color: '#999',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {totalMonth} event{totalMonth !== 1 ? 's' : ''} total
                    </span>
                </div>
            );
        }

        // ── Week view ────────────────────────────────────────────────────────
        const weekStart0 = startOfDay(new Date(selectedDate));
        weekStart0.setDate(weekStart0.getDate() - weekStart0.getDay());

        const year = weekStart0.getFullYear();
        const month = selectedDate.getMonth();
        const firstOfMonth = new Date(year, month, 1);
        const lastOfMonth = new Date(year, month + 1, 0);
        const scanStart = new Date(firstOfMonth);
        scanStart.setDate(scanStart.getDate() - scanStart.getDay());

        const weekChips = [];
        let cursor = new Date(scanStart);
        let weekNum = 0;
        while (cursor <= lastOfMonth) {
            weekNum++;
            const wStart = new Date(cursor);
            const wDays = Array.from({ length: 7 }, (_, i) => {
                const d = new Date(wStart);
                d.setDate(wStart.getDate() + i);
                return d;
            });
            const total = wDays.reduce(
                (s, d) => s + getEventsForDay(d).length,
                0,
            );
            const isCurrentWeek =
                wStart.toDateString() === weekStart0.toDateString();
            if (total > 0)
                weekChips.push({ wStart, total, isCurrentWeek, weekNum });
            cursor.setDate(cursor.getDate() + 7);
        }

        if (weekChips.length === 0) return null;
        const monthLabel = moment(selectedDate).format('MMM YYYY');
        const totalMonth = weekChips.reduce((s, w) => s + w.total, 0);

        return (
            <div
                style={{
                    borderTop: `1px solid ${primaryStr}22`,
                    borderBottom: `1px solid ${primaryStr}22`,
                    backgroundColor: `${primaryStr}07`,
                    padding: '7px 12px',
                    marginBottom: 8,
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 6,
                }}
            >
                <span
                    style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: primaryStr,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        marginRight: 4,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {monthLabel}
                </span>

                {weekChips.map(
                    ({ wStart, total, isCurrentWeek, weekNum: wn }) => (
                        <button
                            key={wn}
                            onClick={() => setSelectedDate(startOfDay(wStart))}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                                padding: '3px 10px',
                                borderRadius: 20,
                                border: `1.5px solid ${isCurrentWeek
                                    ? primaryStr
                                    : primaryStr + '55'
                                    }`,
                                backgroundColor: isCurrentWeek
                                    ? primaryStr
                                    : `${primaryStr}12`,
                                color: isCurrentWeek ? '#fff' : primaryStr,
                                cursor: 'pointer',
                                fontSize: 11,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                transition: 'all 0.15s',
                            }}
                        >
                            <span>Week {wn}</span>
                            <span
                                style={{
                                    backgroundColor: isCurrentWeek
                                        ? 'rgba(255,255,255,0.3)'
                                        : primaryStr,
                                    color: '#fff',
                                    borderRadius: 10,
                                    padding: '0 5px',
                                    fontSize: 10,
                                    fontWeight: 700,
                                    lineHeight: '16px',
                                }}
                            >
                                {total}
                            </span>
                        </button>
                    ),
                )}

                <span
                    style={{
                        marginLeft: 'auto',
                        fontSize: 10,
                        color: '#999',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {totalMonth} event{totalMonth !== 1 ? 's' : ''} total
                </span>
            </div>
        );
    };
    // ── End Event Breakdown Strip ────────────────────────────────────────────

    // ── All Events Breakdown Strip ──────────────────────────────────────────
    const renderAllEventsBreakdown = () => {
        if (!sampleEvents || sampleEvents.length === 0) return null;
        // console.log(sampleEvents, '=======dayMap')
        // Build day-level map: 'YYYY-MM-DD' → count
        const dayMap = {};
        sampleEvents.forEach((ev) => {
            if (!ev.start) return;
            const key = moment(ev.start).format('YYYY-MM-DD');
            dayMap[key] = (dayMap[key] || 0) + 1;
        });

        const sortedDays = Object.keys(dayMap).sort();
        if (sortedDays.length === 0) return null;

        if (view === 'day') {
            const selectedKey = moment(selectedDate).format('YYYY-MM-DD');

            return (
                <div
                    style={{
                        borderTop: `1px solid ${primaryStr}22`,
                        borderBottom: `1px solid ${primaryStr}22`,
                        backgroundColor: `${primaryStr}05`,
                        padding: '7px 12px',
                        marginBottom: 8,
                        display: 'flex',
                        alignItems: 'center',
                        // flexWrap: 'wrap',
                        gap: 6,
                        overflowX: 'scroll',
                    }}
                >
                    <span
                        style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: primaryStr,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            marginRight: 4,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        All Events
                        <br />
                        {/* <span
                            style={{
                                marginLeft: 'auto',
                                fontSize: 10,
                                color: '#999',
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {sampleEvents.length} event
                            {sampleEvents.length !== 1 ? 's' : ''} loaded
                        </span>
                        <br /> */}
                        <span
                            style={{
                                marginLeft: 'auto',
                                fontSize: 10,
                                color: '#999',
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {sampleEvents.length} event
                            {sampleEvents.length !== 1 ? 's' : ''} loaded
                        </span>
                    </span>

                    {sortedDays.map((key) => {
                        console.log('dayMap[key]', dayMap[key], key);
                        const count = dayMap[key];
                        const day = new Date(key);
                        const isSelected = key === selectedKey;
                        return (
                            <button
                                key={key}
                                onClick={() => setSelectedDate(startOfDay(day))}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 5,
                                    padding: '3px 10px',
                                    borderRadius: 20,
                                    border: `1.5px solid ${isSelected
                                        ? primaryStr
                                        : primaryStr + '55'
                                        }`,
                                    backgroundColor: isSelected
                                        ? primaryStr
                                        : `${primaryStr}12`,
                                    color: isSelected ? '#fff' : primaryStr,
                                    cursor: 'pointer',
                                    fontSize: 11,
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    transition: 'all 0.15s',
                                }}
                            >
                                <span>{moment(key).format('D MMM')}</span>
                                <span
                                    style={{
                                        backgroundColor: isSelected
                                            ? 'rgba(255,255,255,0.3)'
                                            : primaryStr,
                                        color: '#fff',
                                        borderRadius: 10,
                                        padding: '0 5px',
                                        fontSize: 10,
                                        fontWeight: 700,
                                        lineHeight: '16px',
                                    }}
                                >
                                    {count}
                                </span>
                            </button>
                        );
                    })}


                </div>
            );
        }

        // ── Week view: group all events by ISO week ──────────────────────────
        const weekStart0 = startOfDay(new Date(selectedDate));
        weekStart0.setDate(weekStart0.getDate() - weekStart0.getDay());

        // Build week map: Sunday-start 'YYYY-Www' → { wStart, total }
        const weekMap = {};
        sampleEvents.forEach((ev) => {
            if (!ev.start) return;
            const d = new Date(ev.start);
            const sun = new Date(d);
            sun.setHours(0, 0, 0, 0);
            sun.setDate(sun.getDate() - sun.getDay()); // back to Sunday
            const wKey = moment(sun).format('GGGG-[W]WW'); // ISO year-week
            if (!weekMap[wKey])
                weekMap[wKey] = { wStart: new Date(sun), total: 0 };
            weekMap[wKey].total += 1;
        });
        console.log(sampleEvents, '=======weekMap');
        const sortedWeeks = Object.keys(weekMap).sort();
        if (sortedWeeks.length === 0) return null;

        const currentWeekKey = moment(weekStart0).format('GGGG-[W]WW');
        const totalAll = sampleEvents.length;

        return (
            <div
                style={{
                    borderTop: `1px solid ${primaryStr}22`,
                    borderBottom: `1px solid ${primaryStr}22`,
                    backgroundColor: `${primaryStr}05`,
                    padding: '7px 12px',
                    marginBottom: 8,
                    display: 'flex',
                    alignItems: 'center',
                    // flexWrap: 'wrap',
                    gap: 6,
                    overflowX: 'scroll',
                }}
            >
                <span
                    style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: primaryStr,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        marginRight: 4,
                        whiteSpace: 'nowrap',
                    }}
                >
                    All Events
                    <br />
                    <span
                        style={{
                            marginLeft: 'auto',
                            fontSize: 10,
                            color: '#999',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {totalAll} event
                        {totalAll !== 1 ? 's' : ''} loaded
                    </span>
                </span>

                {sortedWeeks.map((wKey, idx) => {
                    const { wStart, total } = weekMap[wKey];
                    const isCurrentWeek = wKey === currentWeekKey;
                    const wEnd = new Date(wStart);
                    wEnd.setDate(wStart.getDate() + 6);
                    const label = `${moment(wStart).format('D MMM')} – ${moment(wEnd).format('D MMM')}`;
                    return (
                        <button
                            key={wKey}
                            onClick={() => setSelectedDate(startOfDay(wStart))}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                                padding: '3px 10px',
                                borderRadius: 20,
                                border: `1.5px solid ${isCurrentWeek
                                    ? primaryStr
                                    : primaryStr + '55'
                                    }`,
                                backgroundColor: isCurrentWeek
                                    ? primaryStr
                                    : `${primaryStr}12`,
                                color: isCurrentWeek ? '#fff' : primaryStr,
                                cursor: 'pointer',
                                fontSize: 11,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                transition: 'all 0.15s',
                            }}
                        >
                            <span>{label}</span>
                            <span
                                style={{
                                    backgroundColor: isCurrentWeek
                                        ? 'rgba(255,255,255,0.3)'
                                        : primaryStr,
                                    color: '#fff',
                                    borderRadius: 10,
                                    padding: '0 5px',
                                    fontSize: 10,
                                    fontWeight: 700,
                                    lineHeight: '16px',
                                }}
                            >
                                {total}
                            </span>
                        </button>
                    );
                })}


            </div>
        );
    };
    // ── End All Events Breakdown Strip ──────────────────────────────────────

    return (
        <div className="p-2 md:p-4">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
                <div className="flex items-center gap-2">
                    <button
                        onClick={handlePrev}
                        className="p-1 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
                        style={{ color: primaryStr }}
                    >
                        <FiChevronLeft className="w-5 h-5" />
                    </button>

                    <div className="relative flex items-center justify-center">
                        <ConfigProvider
                            theme={{
                                components: {
                                    DatePicker: {
                                        colorPrimary: primaryStr,
                                        optionSelectedBg: primaryStr,
                                    },
                                },
                            }}
                        >
                            <DatePicker
                                value={dayjs(selectedDate)}
                                format={dateFormat}
                                onChange={(date) => {
                                    if (date)
                                        setSelectedDate(
                                            startOfDay(date.toDate()),
                                        );
                                }}
                                allowClear={false}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                            />
                        </ConfigProvider>
                        <span
                            className="text-base font-medium min-w-[150px] text-center cursor-pointer hover:opacity-80 transition-opacity"
                            style={{ color: primaryStr }}
                        >
                            {formattedDateRange()}
                        </span>
                    </div>

                    <button
                        onClick={handleNext}
                        className="p-1 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
                        style={{ color: primaryStr }}
                    >
                        <FiChevronRight className="w-5 h-5" />
                    </button>

                    {/* Visible event count for current view */}
                    {/* <div
                        className="ml-4 text-sm font-medium px-3 py-1 rounded-full"
                        style={{
                            backgroundColor: `${primaryStr}15`,
                            color: primaryStr,
                        }}
                    >
                        {view === 'day' ? 'Today' : 'Week'}:{' '}
                        <span className="font-bold">{visibleEventCount}</span>{' '}
                        event{visibleEventCount !== 1 ? 's' : ''}
                    </div> */}

                    {/* Total loaded / total available */}
                    {paginationMetadata && (
                        <div
                            className="ml-2 text-sm font-medium px-3 py-1 rounded-full"
                            style={{
                                backgroundColor: `${primaryStr}10`,
                                color: primaryStr,
                            }}
                        >
                            Loaded Records:{' '}
                            <span className="font-bold">
                                {sampleEvents?.length || 0}
                            </span>{' '}
                        </div>
                    )}

                    {/* Initial loading indicator (first page, no paginationMetadata yet).
                        Kept identical to the Load More button in its loading state. */}
                    {isLoadingMore && !paginationMetadata && (
                        <div
                            className="ml-2 text-sm font-semibold px-4 py-1 rounded-full flex items-center gap-2"
                            style={{
                                backgroundColor: `${primaryStr}80`,
                                color: 'white',
                                boxShadow: `0 2px 8px ${primaryStr}40`,
                                cursor: 'not-allowed',
                                minWidth: '100px',
                                justifyContent: 'center',
                            }}
                        >
                            <svg
                                className="animate-spin"
                                style={{ width: 14, height: 14, flexShrink: 0 }}
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v8z"
                                />
                            </svg>
                            Loading...
                        </div>
                    )}

                    {/* Load More button / No More Data */}
                    {paginationMetadata &&
                        onLoadMore &&
                        ((sampleEvents?.length || 0) <
                            (paginationMetadata?.total || 0) ? (
                            <button
                                onClick={onLoadMore}
                                disabled={isLoadingMore}
                                className="ml-2 text-sm font-semibold px-4 py-1 rounded-full transition-all hover:opacity-80 active:scale-95 flex items-center gap-2"
                                style={{
                                    backgroundColor: isLoadingMore
                                        ? `${primaryStr}80`
                                        : primaryStr,
                                    color: 'white',
                                    boxShadow: `0 2px 8px ${primaryStr}40`,
                                    cursor: isLoadingMore
                                        ? 'not-allowed'
                                        : 'pointer',
                                    minWidth: '100px',
                                    justifyContent: 'center',
                                }}
                            >
                                {isLoadingMore ? (
                                    <>
                                        <svg
                                            className="animate-spin"
                                            style={{
                                                width: 14,
                                                height: 14,
                                                flexShrink: 0,
                                            }}
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            />
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8v8z"
                                            />
                                        </svg>
                                        Loading...
                                    </>
                                ) : (
                                    'Load More'
                                )}
                            </button>
                        ) : (
                            <div
                                className="ml-2 text-sm font-medium px-3 py-1 rounded-full"
                                style={{
                                    backgroundColor: '#f0f0f0',
                                    color: '#999',
                                }}
                            >
                                ✓ No More Data
                            </div>
                        ))}
                </div>

                <div
                    className="flex border rounded overflow-hidden shadow-sm"
                    style={{ borderColor: secondaryStr }}
                >
                    <button
                        onClick={() => { setView('day'); onViewChange?.('day'); }}
                        className={`px-6 py-1.5 text-sm font-medium transition-colors border-r ${view === 'day' ? '' : 'bg-white hover:bg-gray-50'}`}
                        style={{
                            borderColor: secondaryStr,
                            ...(view === 'day'
                                ? {
                                    backgroundColor: primaryStr,
                                    color: 'white',
                                }
                                : { color: primaryStr }),
                        }}
                    >
                        Day
                    </button>
                    <button
                        onClick={() => { setView('week'); onViewChange?.('week'); }}
                        className={`px-6 py-1.5 text-sm font-medium transition-colors ${view === 'week' ? '' : 'bg-white hover:bg-gray-50'}`}
                        style={{
                            ...(view === 'week'
                                ? {
                                    backgroundColor: primaryStr,
                                    color: 'white',
                                }
                                : { color: primaryStr }),
                        }}
                    >
                        Week
                    </button>
                </div>
            </div>

            {/* {renderEventBreakdown()} */}
            {renderAllEventsBreakdown()}

            {view === 'day' ? renderDayView() : renderWeekView()}

            {!draggingEvent && hoveredEvent && (
                <div
                    className="z-[9999] w-80 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl"
                    style={{
                        position: 'fixed',
                        top: hoverPosition.y - 10,
                        left: hoverPosition.x,
                        transform: 'translate(-50%, -100%)',
                        pointerEvents: 'none',
                    }}
                >
                    <div className="px-4 pt-4 pb-2 border-b border-gray-100">
                        <div className="text-[12px] text-gray-500">
                            Location Name
                        </div>
                        <div className="mt-1 text-sm font-semibold text-gray-900">
                            {hoveredEvent?.property ||
                                hoveredEvent?.title ||
                                'N/A'}
                        </div>
                    </div>

                    <div className="px-4 py-2 border-b border-gray-100">
                        <div className="text-[12px] text-gray-500">
                            Location
                        </div>
                        <div className="mt-1 text-sm font-semibold text-gray-900">
                            {hoveredEvent?.location || '-'}
                        </div>
                    </div>

                    <div className="px-4 py-2 border-b border-gray-100">
                        <div className="text-[12px] text-gray-500">
                            Position
                        </div>
                        <div className="mt-1 text-sm font-semibold text-gray-900">
                            {hoveredEvent?.Eligibility ||
                                hoveredEvent?.position ||
                                '-'}
                        </div>
                    </div>

                    <div className="px-4 py-2 border-b border-gray-100">
                        <div className="text-[12px] text-gray-500">
                            Schedules
                        </div>
                        <div className="mt-1 text-sm font-semibold text-gray-900">
                            {hoveredEvent?.Schedules?.toLowerCase().includes(
                                'undefined',
                            )
                                ? 'No schedules assigned!'
                                : hoveredEvent?.Schedules || '-'}
                        </div>
                    </div>

                    <div className="px-4 py-2 border-b border-gray-100">
                        <div className="text-[12px] text-gray-500">
                            Assigned By
                        </div>
                        <div className="mt-1 text-sm font-semibold text-gray-900 flex items-center gap-2">
                            {hoveredEvent?.assignedByImage ? (
                                <img
                                    src={hoveredEvent?.assignedByImage}
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                    }}
                                    alt="Assigner"
                                />
                            ) : null}
                            <span>{hoveredEvent?.assignedBy || '-'}</span>
                        </div>
                    </div>

                    <div className="px-4 py-2">
                        <div className="text-[12px] text-gray-500">
                            Duration
                        </div>
                        <div className="mt-1 text-sm font-semibold text-gray-900">
                            {hoveredEvent?.start && hoveredEvent?.end
                                ? `${new Date(
                                    hoveredEvent?.start,
                                ).toLocaleString()}
                            - ${new Date(hoveredEvent?.end).toLocaleString()}
                            `
                                : '-'}
                        </div>
                    </div>

                    {hoveredEvent?.status && (
                        <div className="px-4 pb-4 text-sm text-green-600">
                            {hoveredEvent?.status}
                        </div>
                    )}
                </div>
            )}

            {/* Active event modal */}
            {/* {activeEvent && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white p-4 rounded shadow-lg w-80">
                        <h2 className="text-lg font-bold mb-2">
                            {activeEvent.title}
                        </h2>
                        <p>
                            {activeEvent.start.toString()} -{' '}
                            {activeEvent.end.toString()}
                        </p>
                        <button
                            onClick={() => setActiveEvent(null)}
                            className="mt-3 px-3 py-1 bg-red-500 text-white rounded"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )} */}
            {draggingEvent && ghostPosition && (
                <div
                    className="fixed pointer-events-none z-[9999]"
                    style={{
                        top: ghostPosition.y - 30,
                        left: ghostPosition.x,
                        transform: 'translate(-50%, -100%)',
                    }}
                >
                    {(() => {
                        const durationHours =
                            draggingEvent.durationMs / (1000 * 60 * 60);

                        const widthPercent = (durationHours * 100) / 24;
                        return (
                            <div
                                className="px-2 py-1 rounded-md text-xs font-medium shadow-lg"
                                style={{
                                    background: 'rgba(34,197,94,0.85)',
                                    color: 'white',
                                    width: `${widthPercent}px`, // tweak multiplier if needed
                                }}
                            >
                                {view === 'day'
                                    ? moment()
                                        .hour(ghostPosition?.SlotTimeDate)
                                        .minute(0)
                                        .format(timeFormat)
                                    : //  == 0
                                    //     ? '12am'
                                    //     : ghostPosition.SlotTimeDate < 12
                                    //         ? `${ghostPosition.SlotTimeDate}am`
                                    //         : ghostPosition.SlotTimeDate == 12
                                    //             ? '12pm'
                                    //             : `${ghostPosition.SlotTimeDate - 12}pm`
                                    ghostPosition.SlotTimeDate}
                            </div>
                        );
                    })()}
                </div>
            )}
        </div>
    );
}
